import {
  CURRENCIES,
  type CreditCard,
  type Currency,
  type Offer,
} from "@/data/schemas";
import { DIRECT_CASHBACK_VALUATIONS } from "@/data/valuations";
import type { Filters } from "@/server/api/routers/schemas";
import {
  getOfferFirstYearValue,
  percentOfHistoricalMax,
  VALUATION_METHODS,
} from "@/utils/calculations";
import { cardCountsTowards524 } from "@/utils/fiveTwentyFour";

const normalize = (name: string) =>
  name.toLowerCase().replace(/[^A-Za-z]/g, "");

export const cardMatchesFilters = (card: CreditCard, filters: Filters) => {
  if (!normalize(card.name).includes(normalize(filters.name))) return false;

  const countsTowards524 = cardCountsTowards524(card);
  if (
    !countsTowards524 &&
    !filters.showCardsThatCountTowards524.includes("No")
  ) {
    return false;
  }
  if (
    countsTowards524 &&
    !filters.showCardsThatCountTowards524.includes("Yes")
  ) {
    return false;
  }

  // Rewards vs. Cashback
  const cashbackCurrencies: Currency[] = [
    CURRENCIES.AMERICAN_EXPRESS,
    CURRENCIES.BANK_OF_AMERICA,
    CURRENCIES.BARCLAYS,
    CURRENCIES.BILT,
    CURRENCIES.BREX,
    CURRENCIES.CHASE,
    CURRENCIES.CITI,
    CURRENCIES.CAPITAL_ONE,
    CURRENCIES.DISCOVER,
    CURRENCIES.US_BANK,
    CURRENCIES.WELLS_FARGO,
    CURRENCIES.USD,
  ];
  if (cashbackCurrencies.includes(card.currency)) {
    if (!filters.travelOrCashback.includes("Cashback")) {
      return false;
    }
  } else if (!filters.travelOrCashback.includes("Travel")) {
    return false;
  }

  //* isBusiness
  if (!filters.businessOrPersonal.includes("Business") && card.isBusiness)
    return false;
  if (!filters.businessOrPersonal.includes("Personal") && !card.isBusiness)
    return false;

  //* Min Annual Fee
  if (
    (filters.minimumAnnualFee || filters.minimumAnnualFee === 0) &&
    card.annualFee < filters.minimumAnnualFee
  ) {
    return false;
  }

  //* Max Annual Fee
  if (
    (filters.maximumAnnualFee || filters.maximumAnnualFee === 0) &&
    card.annualFee > filters.maximumAnnualFee
  ) {
    return false;
  }

  //* Valuation Method - Direct Cashback
  if (
    filters.valuationMethod === VALUATION_METHODS.CASH_REDEMPTION &&
    !Object.keys(DIRECT_CASHBACK_VALUATIONS).includes(card.currency)
  ) {
    return false;
  }

  // Offer Value, Spend, etc.
  if (!card.offers.some((offer) => offerMatchesFilters(offer, filters, card)))
    return false;

  if (!filters.issuers.includes(card.issuer)) return false;
  if (!filters.networks.includes(card.network)) return false;

  return true;
};

export const offerMatchesFilters = (
  offer: Offer,
  filters: Filters,
  card: CreditCard,
) => {
  //* Monthly Spend
  const requiredMonthlySpend = offer.spend / (offer.days / 30);
  // 10,000 / (90 / 30) = 3,333.33
  if (
    (filters.maximumMonthlySpend || filters.maximumMonthlySpend === 0) &&
    (filters.maximumMonthlySpend ?? Number.POSITIVE_INFINITY) <
      requiredMonthlySpend
  ) {
    console.debug(
      `Filtering out card ${card.name}, offer ${JSON.stringify(
        offer,
      )} because it requires ${requiredMonthlySpend} spend per month but you can afford ${
        filters.maximumMonthlySpend
      }`,
    );
    return false;
  }

  // Minimum / Maximum Value
  const value = getOfferFirstYearValue(offer, card).value;
  if (
    (filters.minimumValue || filters.minimumValue === 0) &&
    value < filters.minimumValue
  ) {
    console.debug(
      `Filtering out card ${card.name} because it has a value of ${value} which is less than the minimum value of ${filters.minimumValue}`,
    );
    return false;
  }

  //* Minimum Spend
  if (
    (filters.minimumSpend || filters.minimumSpend === 0) &&
    offer.spend < (filters.minimumSpend || 0)
  ) {
    console.debug(
      `Filtering out card ${card.name} because it has a spend of ${offer.spend} which is less than the minimum spend of ${filters.minimumSpend}`,
    );
    return false;
  }

  //* Maximum Spend
  if (filters.maximumSpend && offer.spend > (filters.maximumSpend || 0)) {
    console.debug(
      `Filtering out card ${card.name} because it has a spend of ${offer.spend} which is more than the maximum spend of ${filters.maximumSpend}`,
    );
    return false;
  }

  //* Minimum Value
  if (
    filters.minimumValue &&
    getOfferFirstYearValue(offer, card).value <
      (filters.minimumValue ?? Number.NEGATIVE_INFINITY)
  ) {
    console.debug(
      `Filtering out card ${card.name} because it has a value of ${value} which is less than the minimum value of ${filters.minimumValue}`,
    );
    return false;
  }

  //* Minimum Percent of Historical Max
  // English: Return true if at least one offer is above the quality threshold
  const percent = percentOfHistoricalMax(offer, card);
  switch (filters.minimumPercentOfHistoricalMax ?? 0) {
    case "Best Ever": {
      if (percent < 1) return false;
      break;
    }
    case "Great (95%)": {
      if (percent < 0.95) return false;
      break;
    }
    case "Good (80%)": {
      if (percent < 0.8) return false;
      break;
    }
    case "Decent (60%)": {
      if (percent < 0.6) return false;
      break;
    }
    default: {
      // "All" goes in here - we do nothing()
      break;
    }
  }

  return true;
};
